'use client';

import { useState, useEffect } from 'react';

const CONSENT_KEY = 'mb_cookie_consent';

export type ConsentStatus = 'accepted' | 'declined' | null;

export function getConsentStatus(): ConsentStatus {
  if (typeof window === 'undefined') return null;
  const val = localStorage.getItem(CONSENT_KEY);
  if (val === 'accepted' || val === 'declined') return val;
  return null;
}

interface CookieConsentBannerProps {
  onConsent: (accepted: boolean) => void;
}

export default function CookieConsentBanner({ onConsent }: CookieConsentBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const handle = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(handle);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setIsDismissed(true);
    onConsent(true);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setIsDismissed(true);
    onConsent(false);
  };

  const visible = mounted && !isDismissed && !getConsentStatus();

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        backgroundColor: '#1a1a2e',
        color: '#e0e0e0',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
        boxShadow: '0 -2px 16px rgba(0,0,0,0.3)',
        fontFamily: 'var(--font-plus-jakarta-sans), Inter, sans-serif',
        fontSize: '14px',
        lineHeight: '1.5',
      }}
    >
      <p style={{ margin: 0, flex: 1, minWidth: '280px' }}>
        We use essential cookies to make our site work. With your consent, we may
        also use non-essential cookies to improve user experience, personalize
        advertisements, and analyze web traffic. You can change your cookie
        settings at any time by clicking &ldquo;Preferences&rdquo; in our{' '}
        {/* /privacy-policy has never existed — the published route is /privacy. */}
        <a
          href='/privacy'
          style={{ color: '#60a5fa', textDecoration: 'underline' }}
        >
          Cookie Policy
        </a>
        .
      </p>
      <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
        <button
          onClick={handleDecline}
          style={{
            padding: '8px 20px',
            minHeight: '44px',
            borderRadius: '6px',
            border: '1px solid #444',
            backgroundColor: 'transparent',
            color: '#e0e0e0',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          Decline
        </button>
        <button
          onClick={handleAccept}
          style={{
            padding: '8px 20px',
            minHeight: '44px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: '#2563eb',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          Accept
        </button>
      </div>
    </div>
  );
}
